import React, { useMemo, useState, useEffect } from "react";
import { Lock, Star, Trash2, History, Plus, MessageSquare } from "lucide-react";
import { apiCall, getErrorMessage } from "./utils/api";
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp, 
  doc, 
  deleteDoc 
} from 'firebase/firestore';
import { db } from './firebase'; 

/* ===============================
   Resume → text formatter
================================ */
const formatResumeDataAsText = (data) => {
  if (!data || !data.personalInfo) return "";
  let text = `Name: ${data.personalInfo.name}
Email: ${data.personalInfo.email}
Phone: ${data.personalInfo.phone}
Location: ${data.personalInfo.location}\n\n`;

  if (data.goal) text += `Goal:\n${data.goal}\n\n`;

  text += "Work Experience:\n";
  (data.experience || []).forEach(exp => {
    text += `- ${exp.title || ""} at ${exp.company || ""} (${exp.period || ""})\n${exp.description || ""}\n`;
  });

  text += "\nEducation:\n";
  (data.education || []).forEach(edu => {
    text += `- ${edu.degree || ""} from ${edu.school || ""} (${edu.period || ""})\n`;
  });

  text += "\nSkills:\n";
  const skills = Array.isArray(data.skills)
    ? data.skills
    : typeof data.skills === "string"
      ? data.skills.split(",")
      : [];
  text += skills.join(", ");

  return text;
};

export default function InterviewQuestions({
  resumeData,
  jobDescription,
  userData,
  user,
  preloadedHistoryItem // ADDED THIS PROP
}) {
  /* ===============================
     State
  ================================ */
  const [jobText, setJobText] = useState(jobDescription || "");
  const [jobInsights, setJobInsights] = useState(null);
  const [questionsSections, setQuestionsSections] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingLabel, setLoadingLabel] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  // Upgrade gating & History
  const [usage, setUsage] = useState(null);
  const [usageLoading, setUsageLoading] = useState(false);
  const [usageError, setUsageError] = useState(null);
  const [history, setHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isHistoryView, setIsHistoryView] = useState(false);

  /* ===============================
      NEW: Sync Preloaded Data
      This logic detects if we came from the Dashboard "View Q&A"
  ================================ */
  useEffect(() => {
    if (preloadedHistoryItem) {
      setQuestionsSections(preloadedHistoryItem.sections || []);
      setJobText(preloadedHistoryItem.jobDescription || "");
      setIsHistoryView(false); // Ensure we are NOT in the history list view
      setActiveTab(0);
      setCurrentPage(1);
    }
  }, [preloadedHistoryItem]);

  /* ===============================
     Sync JD from props
  ================================ */
  useEffect(() => {
    if (jobDescription && jobDescription !== jobText) {
      setJobText(jobDescription);
    }
  }, [jobDescription]);

  /* ===============================
     Usage gating
  ================================ */
  useEffect(() => {
    if (!user?.uid) return;
    let cancelled = false;

    (async () => {
      try {
        setUsageLoading(true);
        const { data } = await apiCall('/api/usage/me');
        if (!cancelled) setUsage(data || {});
      } catch {
        if (!cancelled) setUsageError("Failed to load usage.");
      } finally {
        if (!cancelled) setUsageLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [user?.uid]);

  /* ===============================
     History Subscription
  ================================ */
  useEffect(() => {
    if (!user?.uid) return;

    const q = query(
      collection(db, `users/${user.uid}/saved_qna`),
      orderBy("createdAt", "desc")
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const historyData = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setHistory(historyData);
      setIsLoadingHistory(false);
    }, (error) => {
        console.error("Firestore error:", error);
        setIsLoadingHistory(false);
    });

    return () => unsubscribe();
  }, [user?.uid]);

  const deleteHistoryItem = async (e, id) => {
    e.stopPropagation();
    if (window.confirm("Delete this session?")) {
      try {
        await deleteDoc(doc(db, `users/${user.uid}/saved_qna`, id));
      } catch (err) { alert("Delete failed"); }
    }
  };

  const monthlyTotalTokens = useMemo(() => {
    if (!usage) return 0;
    return Object.values(usage)
      .filter(v => typeof v === "object")
      .reduce((sum, v) => {
        if (typeof v.total_tokens === "number") return sum + v.total_tokens;
        return sum + (v.in_tokens || 0) + (v.out_tokens || 0);
      }, 0);
  }, [usage]);

  const isBasic = (userData?.role || "").toLowerCase() === "basic";
  const shouldRestrict = isBasic && !usageLoading && monthlyTotalTokens > 8000000;

  /* ===============================
     Pagination Logic
  ================================ */
  const currentQuestions = useMemo(() => {
    if (!questionsSections || !questionsSections.length) return [];
    return questionsSections[activeTab]?.questions || [];
  }, [questionsSections, activeTab]);

  const totalPages = Math.max(1, Math.ceil(currentQuestions.length / pageSize));
  const pageItems = currentQuestions.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  /* ===============================
     AI Actions
  ================================ */
  const analyzeJob = async () => {
    if (!jobText.trim()) return;
    setLoading(true);
    setLoadingLabel("Analyzing job description…");
    setJobInsights(null);

    try {
      const { data } = await apiCall("/api/jobs/analyze-ai", {
        method: "POST",
        body: JSON.stringify({ job_description: jobText }),
        headers: { "Content-Type": "application/json" }
      });
      setJobInsights(data);
    } catch (err) {
      alert(getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoadingLabel("");
    }
  };

  const generateQuestions = async () => {
    if (!jobText.trim()) return;
    setLoading(true);
    setLoadingLabel("Generating interview questions…");
    setQuestionsSections([]);

    try {
      const fd = new FormData();
      fd.append("job_description_text", jobText);
      const resumeText = formatResumeDataAsText(resumeData);
      if (resumeText) fd.append("resume_text", resumeText);

      const { data } = await apiCall("/api/generate-categorized-qna", {
        method: "POST",
        body: fd
      });

      const sections = data.sections || [];
      setQuestionsSections(sections);

      // Save to Firestore History
      if (sections.length > 0 && user?.uid) {
        await addDoc(collection(db, `users/${user.uid}/saved_qna`), {
          title: jobInsights?.title_company || (jobText.split('\n')[0].substring(0, 40) + "..."),
          sections: sections,
          jobDescription: jobText,
          createdAt: serverTimestamp(),
        });
      }

      if (sections.length > 0) setActiveTab(0);
      setCurrentPage(1);
    } catch (err) {
      setQuestionsSections([]);
      alert(getErrorMessage(err));
    } finally {
      setLoading(false);
      setLoadingLabel("");
    }
  };

  if (shouldRestrict) {
    return (
      <div className="p-8 text-center">
        <Lock size={56} className="mx-auto mb-4" />
        <h2 className="text-xl font-bold">Upgrade Required</h2>
        <p className="text-sm mt-2">Monthly usage exceeded 10,000 tokens.</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen bg-[#fafafa] p-8 rounded-xl">
      {/* LOADER OVERLAY */}
      {loading && (
        <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm rounded-xl">
          <div className="flex flex-col items-center bg-white px-8 py-6 rounded-xl shadow-xl">
            <div className="w-10 h-10 border-4 border-[#3C0F6C]/30 border-t-[#3C0F6C] rounded-full animate-spin" />
            <p className="mt-4 text-sm font-medium text-[#3C0F6C]">{loadingLabel}</p>
          </div>
        </div>
      )}

      {/* HEADER WITH TOGGLE */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-[#3C0F6C]">AI Interview Questions</h1>
        <div className="flex gap-2">
          <button 
            onClick={() => setIsHistoryView(false)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${!isHistoryView ? 'bg-[#3C0F6C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            <Plus size={18} /> New
          </button>
          <button 
            onClick={() => setIsHistoryView(true)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition ${isHistoryView ? 'bg-[#3C0F6C] text-white' : 'bg-gray-200 text-gray-700'}`}
          >
            <History size={18} /> History
          </button>
        </div>
      </div>

      {isHistoryView ? (
        /* ===============================
           HISTORY LIST VIEW
        ================================ */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {history.map((item) => (
            <div 
              key={item.id} 
              onClick={() => {
                setQuestionsSections(item.sections);
                setJobText(item.jobDescription);
                setIsHistoryView(false);
                setCurrentPage(1);
              }}
              className="p-5 bg-white border border-gray-200 rounded-xl hover:border-[#3C0F6C] cursor-pointer relative group transition-all shadow-sm"
            >
              <h3 className="font-bold text-[#3C0F6C] pr-8 line-clamp-2">{item.title}</h3>
              <p className="text-xs text-gray-500 mt-3 flex items-center gap-1">
                <History size={12} />
                {item.createdAt?.seconds ? new Date(item.createdAt.seconds * 1000).toLocaleDateString() : 'Saving...'}
              </p>
              <button 
                onClick={(e) => deleteHistoryItem(e, item.id)} 
                className="absolute top-4 right-4 text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ))}
          {!isLoadingHistory && history.length === 0 && (
            <div className="col-span-full text-center py-20 text-gray-400 bg-white border rounded-xl border-dashed">
              <MessageSquare size={40} className="mx-auto mb-2 opacity-20" />
              <p>No saved interview questions yet.</p>
            </div>
          )}
        </div>
      ) : (
        /* ===============================
           GENERATOR VIEW (ORIGINAL UI)
        ================================ */
        <>
          {/* JD INPUT AREA */}
          <div className="flex gap-3 mb-6">
            <textarea
              value={jobText}
              onChange={(e) => setJobText(e.target.value)}
              placeholder="Paste job description here..."
              className="flex-1 min-h-[140px] border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-[#3C0F6C] focus:border-transparent outline-none transition-all"
            />
            <button
              onClick={analyzeJob}
              disabled={loading || !jobText.trim()}
              className="bg-[#3C0F6C] text-white px-6 py-3 rounded-lg disabled:opacity-50 hover:bg-[#2d0b52] transition-colors self-start"
            >
              Analyze
            </button>
          </div>

          {/* AI ANALYSIS INSIGHTS */}
          {jobInsights && (
            <div className="bg-[#ede7f6] p-5 rounded-lg mb-6 space-y-3 text-sm border border-[#3C0F6C]/10">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><b>Title:</b> {jobInsights.title_company}</div>
                <div><b>Type:</b> {jobInsights.job_type}</div>
                <div><b>Locations:</b> {jobInsights.locations?.join(", ")}</div>
                <div>
                  <b>Experience:</b> {jobInsights.experience_level}
                  {jobInsights.experience_years && ` (${jobInsights.experience_years}+ yrs)`}
                </div>
              </div>
              <div>
                <b>Skills:</b>{" "}
                {jobInsights.required_skills?.map((s, idx) => (
                  <span key={idx} className="inline-block bg-white px-2 py-0.5 rounded border border-[#3C0F6C]/20 mr-1 mb-1 text-xs font-medium">{s}</span>
                ))}
              </div>
              <div>
                <b>Responsibilities:</b>
                <ul className="list-disc pl-5 mt-1 space-y-1">
                  {jobInsights.responsibilities?.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
              <div className="pt-2 border-t border-[#3C0F6C]/10 italic">
                <b>Summary:</b> {jobInsights.one_line_summary}
              </div>
            </div>
          )}

          {/* GENERATE BUTTON */}
          <div className="mb-8">
            <button
              onClick={generateQuestions}
              disabled={loading || !jobText.trim()}
              className="bg-[#3C0F6C] text-white px-8 py-3 rounded-lg disabled:opacity-50 hover:bg-[#2d0b52] transition-colors shadow-md"
            >
              Generate AI Interview Q&A
            </button>
          </div>

          {/* Q&A DISPLAY AREA */}
          {questionsSections.length > 0 && (
            <div className="mt-8">
              {/* TABS */}
              <div className="flex border-b border-gray-200 mb-6 overflow-x-auto scrollbar-hide">
                {questionsSections.map((section, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setActiveTab(idx);
                      setCurrentPage(1);
                    }}
                    className={`px-6 py-3 border-b-2 font-bold text-sm whitespace-nowrap transition-all ${
                      activeTab === idx
                      ? "border-[#3C0F6C] text-[#3C0F6C] bg-[#3C0F6C]/5"
                      : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {section.category}
                  </button>
                ))}
              </div>

              {/* QUESTION LIST */}
              <div className="space-y-4">
                {pageItems.map((q, i) => (
                  <div key={i} className="bg-white p-6 rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="font-bold text-[#1a1a1a] text-lg mb-3">
                      <span className="text-[#3C0F6C] mr-2">Q{(currentPage - 1) * pageSize + i + 1}.</span>
                      {q.question}
                    </div>
                    <div className="text-gray-700 leading-relaxed whitespace-pre-wrap pl-8 border-l-2 border-[#3C0F6C]/10 italic">
                      {q.answer}
                    </div>
                  </div>
                ))}
              </div>

              {/* PAGINATION */}
              {totalPages > 1 && (
                <div className="flex justify-center items-center gap-4 mt-10">
                  <button
                    className="p-2 border rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(p => p - 1)}
                  >
                    Prev
                  </button>
                  <span className="text-sm font-bold text-[#3C0F6C]">
                    Page {currentPage} of {totalPages}
                  </span>
                  <button
                    className="p-2 border rounded-full hover:bg-gray-100 disabled:opacity-30 transition-colors"
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(p => p + 1)}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}